import { useState } from 'react';
import type { AppointmentRow } from '@/hooks/useAppointments';
import { StatusBadge, PaymentBadge } from '@/components/base/StatusBadge';
import ConfirmCancelModal from './ConfirmCancelModal';
import CompleteServiceModal from './CompleteServiceModal';

type ActionType = 'no_show' | 'cancel' | 'confirm';

interface Props {
  appointments: AppointmentRow[];
  loading: boolean;
  cancelling: string | null;
  completing: string | null;
  onCancel: (id: string) => Promise<boolean>;
  onNoShow?: (id: string) => Promise<boolean>;
  onComplete?: (id: string, method: 'cash' | 'transfer') => Promise<{ ok: boolean; error?: string }>;
  onConfirm?: (id: string) => Promise<boolean>;
}

// ... existing code ...

export default function AppointmentsTable({ appointments, loading, cancelling, completing, onCancel, onNoShow, onComplete, onConfirm }: Props) {
  const [pendingAction, setPendingAction] = useState<{ appt: AppointmentRow; type: ActionType } | null>(null);
  const [completeTarget, setCompleteTarget] = useState<AppointmentRow | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleNoShowClick = (appt: AppointmentRow) => {
    setPendingAction({ appt, type: 'no_show' });
  };

  const handleConfirmClick = (appt: AppointmentRow) => {
    setPendingAction({ appt, type: 'confirm' });
  };

  const handleCopyContact = (appt: AppointmentRow) => {
    const text = [appt.client_name, appt.client_email, appt.client_phone].filter(Boolean).join(' | ');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(appt.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    if (pendingAction.type === 'no_show' && onNoShow) {
      await onNoShow(pendingAction.appt.id);
    } else if (pendingAction.type === 'confirm' && onConfirm) {
      setConfirming(pendingAction.appt.id);
      await onConfirm(pendingAction.appt.id);
      setConfirming(null);
    } else {
      await onCancel(pendingAction.appt.id);
    }
    setPendingAction(null);
  };

  const handleCompleteConfirm = async (method: 'cash' | 'transfer') => {
    if (!completeTarget || !onComplete) return;
    setCompleteError(null);
    const result = await onComplete(completeTarget.id, method);
    if (result.ok) {
      setCompleteTarget(null);
    } else {
      setCompleteError(result.error ?? 'Error al completar el servicio');
    }
  };

  return (
    <>
      <div className="bg-[#141414] border border-white/8 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Cliente</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden md:table-cell">Email</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden lg:table-cell">Servicio</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Fecha</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden sm:table-cell">Hora</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Estado</th>
                <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden xl:table-cell">Pago</th>
                <th className="text-right text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-white/5 animate-pulse">
                      <td className="px-5 py-4"><div className="h-3.5 bg-white/8 rounded w-28" /></td>
                      <td className="px-5 py-4 hidden md:table-cell"><div className="h-3 bg-white/6 rounded w-36" /></td>
                      <td className="px-5 py-4 hidden lg:table-cell"><div className="h-3 bg-white/6 rounded w-24" /></td>
                      <td className="px-5 py-4"><div className="h-3 bg-white/6 rounded w-20" /></td>
                      <td className="px-5 py-4 hidden sm:table-cell"><div className="h-3 bg-white/6 rounded w-12" /></td>
                      <td className="px-5 py-4"><div className="h-5 bg-white/8 rounded-full w-20" /></td>
                      <td className="px-5 py-4 hidden xl:table-cell"><div className="h-5 bg-white/6 rounded w-16" /></td>
                      <td className="px-5 py-4 text-right"><div className="h-7 bg-white/6 rounded-lg w-24 ml-auto" /></td>
                    </tr>
                  ))}
                </>
              )}

              {!loading && appointments.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
                        <i className="ri-calendar-line text-white/25 text-xl" />
                      </div>
                      <p className="text-white/30 text-sm">No se encontraron citas</p>
                      <p className="text-white/20 text-xs">Prueba ajustando los filtros</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && appointments.map((appt, idx) => (
                <tr
                  key={appt.id}
                  className={`border-b border-white/5 hover:bg-white/2 transition-colors ${idx === appointments.length - 1 ? 'border-b-0' : ''}`}
                >
                  <td className="px-5 py-4">
                    <span className="text-white text-sm font-medium">{appt.client_name || '—'}</span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <div>
                      <span className="text-white/45 text-sm">{appt.client_email || '—'}</span>
                      {appt.client_phone && (
                        <p className="text-white/30 text-xs mt-0.5 flex items-center gap-1">
                          <i className="ri-phone-line text-[10px]" />
                          {appt.client_phone}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className="text-white/60 text-sm">{appt.services?.name ?? '—'}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-white/70 text-sm font-mono">
                      {appt.appointment_date ? (() => {
                        const [y, m, d] = appt.appointment_date.split('-');
                        return `${d}/${m}/${y}`;
                      })() : '—'}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className="text-white/50 text-sm font-mono">{appt.start_time?.slice(0, 5) ?? '—'}</span>
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={appt.status} paymentStatus={appt.payment_status} />
                  </td>
                  <td className="px-5 py-4 hidden xl:table-cell">
                    <PaymentBadge status={appt.payment_status} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    {appt.status === 'confirmed' && (
                      <div className="flex items-center justify-end gap-2">
                        {/* Complete service */}
                        <button
                          onClick={() => setCompleteTarget(appt)}
                          disabled={completing === appt.id || cancelling === appt.id}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(16,185,129,0.10)',
                            border: '1px solid rgba(16,185,129,0.22)',
                            color: '#34d399',
                          }}
                        >
                          {completing === appt.id ? (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Guardando
                            </>
                          ) : (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-check-double-line text-xs" />
                              </div>
                              Completar
                            </>
                          )}
                        </button>

                        {/* No llegó */}
                        <button
                          onClick={() => handleNoShowClick(appt)}
                          disabled={cancelling === appt.id || completing === appt.id}
                          className="inline-flex items-center gap-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 hover:border-amber-500/35 text-amber-400 hover:text-amber-300 text-xs font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {cancelling === appt.id ? (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Guardando
                            </>
                          ) : (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-user-unfollow-line text-xs" />
                              </div>
                              No llegó
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {appt.status === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        {/* Confirmar manualmente */}
                        <button
                          onClick={() => handleConfirmClick(appt)}
                          disabled={confirming === appt.id || cancelling === appt.id}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{
                            backgroundColor: 'rgba(16,185,129,0.10)',
                            border: '1px solid rgba(16,185,129,0.22)',
                            color: '#34d399',
                          }}
                        >
                          {confirming === appt.id ? (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Confirmando
                            </>
                          ) : (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-checkbox-circle-line text-xs" />
                              </div>
                              Confirmar
                            </>
                          )}
                        </button>

                        {/* Copiar contacto */}
                        <button
                          onClick={() => handleCopyContact(appt)}
                          title="Copiar datos de contacto"
                          className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/45 hover:text-white/70 text-xs font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                        >
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className={`${copiedId === appt.id ? 'ri-check-line text-emerald-400' : 'ri-contacts-line'} text-xs`} />
                          </div>
                          {copiedId === appt.id ? 'Copiado' : 'Contacto'}
                        </button>

                        {/* Cancelar */}
                        <button
                          onClick={() => setPendingAction({ appt, type: 'cancel' })}
                          disabled={cancelling === appt.id || confirming === appt.id}
                          className="inline-flex items-center gap-1.5 bg-red-500/8 hover:bg-red-500/15 border border-red-500/15 hover:border-red-500/30 text-red-400/70 hover:text-red-400 text-xs font-medium px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {cancelling === appt.id ? (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-loader-4-line text-xs animate-spin" />
                              </div>
                              Cancelando
                            </>
                          ) : (
                            <>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-close-circle-line text-xs" />
                              </div>
                              Cancelar
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {!['confirmed', 'pending'].includes(appt.status) && (
                      <span className="text-white/20 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error toast for complete */}
      {completeError && (
        <div className="fixed bottom-6 right-6 z-[70] flex items-center gap-3 bg-red-500/15 border border-red-500/25 text-red-400 text-sm px-4 py-3 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-sm" />
          </div>
          {completeError}
          <button onClick={() => setCompleteError(null)} className="ml-2 cursor-pointer">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Confirm no-show modal */}
      {pendingAction && (
        <ConfirmCancelModal
          clientName={pendingAction.appt.client_name}
          actionType={pendingAction.type}
          loading={cancelling === pendingAction.appt.id}
          onConfirm={handleConfirm}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {/* Complete service modal */}
      <CompleteServiceModal
        appointment={completeTarget}
        completing={completing === completeTarget?.id}
        onConfirm={handleCompleteConfirm}
        onClose={() => { setCompleteTarget(null); setCompleteError(null); }}
      />
    </>
  );
}
